import { CaretLeftIcon, MinusIcon, PlusIcon, StarFourIcon } from '@phosphor-icons/react'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { Button } from '@/components/business/market-shop/button'
import { NoteTextarea } from '@/components/business/market-shop/note-textarea'
import { PopupWindow } from '@/components/business/market-shop/popup-window'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { ZoomFrame } from '@/components/ui/zoomable-image'
import { PopupRequiredProps } from '@/factories'
import { MarketShopProductModel } from '@/models'
import { MAX_NOTE_LENGTH } from '@/pages/market-shop/market-shop.constants'
import { useMarketShopStore } from '@/pages/market-shop/market-shop.store'
import { getMarketShopImageHref } from '@/pages/market-shop/market-shop.utils'
import { formatPrice } from '@/utils'

import { DESCRIPTION_COLLAPSED_HEIGHT_PX } from './product-details.constants'
import * as S from './product-details.styled'

export type ProductDetailsProps = PopupRequiredProps & {
  product: MarketShopProductModel
}

export const ProductDetails = ({ product, onClose }: ProductDetailsProps) => {
  const { t } = useTranslation(['market-shop'])
  const marketShopStore = useMarketShopStore()
  const isShopOpened = marketShopStore.isOpened
  const isAlreadyInCart = marketShopStore.getQuantity(product) > 0

  const [localQuantity, setLocalQuantity] = useState(() => {
    return marketShopStore.cart[product.id]?.quantity ?? 1
  })
  const [localNote, setLocalNote] = useState(() => {
    return marketShopStore.cart[product.id]?.note ?? ''
  })
  const [isBodyExpanded, setIsBodyExpanded] = useState(false)
  const [showBodyToggle, setShowBodyToggle] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)

  const discountTag = product.tags.find((tag) => tag.name === 'Discount')
  const newTag = product.tags.find((tag) => tag.name === 'NEW')

  const { isResidentFlow } = useGetSettingsByToken()

  useLayoutEffect(() => {
    const el = descriptionRef.current
    if (!el) return

    const checkOverflow = () => {
      if (!isBodyExpanded) {
        setShowBodyToggle(el.scrollHeight > el.clientHeight)
      } else {
        setShowBodyToggle(el.scrollHeight > DESCRIPTION_COLLAPSED_HEIGHT_PX)
      }
    }

    checkOverflow()

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)

    return () => observer.disconnect()
  }, [product.description, isBodyExpanded])

  const handleResidentAddToCart = () => {
    marketShopStore.setProductQuantity(product, localQuantity, localNote)
    onClose()
  }

  return (
    <PopupWindow lightBg={true} onClose={onClose}>
      {({ isInTransition }) => (
        <>
          <S.Content>
            <S.ImageContainer>
              {product.image ? (
                <ZoomFrame
                  src={getMarketShopImageHref(product.image)}
                  title={product.summary}
                  alt={product.summary}
                  style={{ width: '100%' }}
                >
                  <S.Image src={getMarketShopImageHref(product.image)} alt={product.summary} />
                </ZoomFrame>
              ) : (
                <S.PlaceholderIcon icon={StarFourIcon} size="6.8rem" />
              )}
              {(discountTag || newTag) && (
                <S.TagsColumn>
                  {discountTag && (
                    <S.DiscountChip variant="red">-{discountTag.discount}%</S.DiscountChip>
                  )}
                  {newTag && (
                    <S.NewChip variant="green">{t('market-shop:productCard.newTag')}</S.NewChip>
                  )}
                </S.TagsColumn>
              )}
            </S.ImageContainer>
            <S.Title forwardedAs="h2" size="heading-small" weight="semibold" color="black800">
              {product.summary}
            </S.Title>

            <S.DescriptionText
              ref={descriptionRef}
              $isExpanded={isBodyExpanded}
              forwardedAs="p"
              size="text-medium"
              color="black500"
            >
              {!isBodyExpanded && showBodyToggle && (
                <S.ShowMoreButton onClick={() => setIsBodyExpanded(true)}>
                  <S.Ellipsis>...</S.Ellipsis>
                  <S.ButtonText>{t('market-shop:productDetails.showMore')}</S.ButtonText>
                </S.ShowMoreButton>
              )}
              {product.description || '-'}
              {isBodyExpanded && showBodyToggle && (
                <S.ShowMoreButton $isInline onClick={() => setIsBodyExpanded(false)}>
                  <S.ButtonText>{t('market-shop:productDetails.showLess')}</S.ButtonText>
                </S.ShowMoreButton>
              )}
            </S.DescriptionText>

            <S.PriceGroup>
              {product.discountedPrice !== product.price && (
                <S.OriginalPrice
                  forwardedAs="span"
                  size="heading-large"
                  color="black500"
                  weight="regular"
                >
                  {formatPrice(product.price, { zeroPrice: true })}
                </S.OriginalPrice>
              )}
              <Typography as="span" size="heading-large" weight="semibold" color="black800">
                {formatPrice(
                  product.discountedPrice !== product.price
                    ? product.discountedPrice
                    : product.price,
                  { zeroPrice: true },
                )}
              </Typography>
            </S.PriceGroup>

            {isShopOpened && (
              <S.HowManyContainer>
                <Typography
                  as="span"
                  size="text-large"
                  color="black800"
                  weight="semibold"
                  textAlign="center"
                >
                  {t('market-shop:productDetails.howMany')}
                </Typography>
                <S.HowManyControls>
                  <Button
                    onlyBox
                    use="secondary-gray"
                    size="large"
                    disabled={isInTransition || localQuantity <= Number(!isAlreadyInCart)}
                    onClick={() => setLocalQuantity((q) => q - 1)}
                  >
                    <Icon icon={MinusIcon} />
                  </Button>
                  <S.Count
                    forwardedAs="span"
                    size="heading-huge"
                    color="black800"
                    weight="semibold"
                  >
                    {localQuantity}
                  </S.Count>
                  <Button
                    onlyBox
                    use="primary"
                    size="large"
                    disabled={
                      isInTransition ||
                      localQuantity >= (product.unlimited ? Infinity : (product?.quantity ?? 0))
                    }
                    onClick={() => setLocalQuantity((q) => q + 1)}
                  >
                    <Icon icon={PlusIcon} />
                  </Button>
                </S.HowManyControls>
              </S.HowManyContainer>
            )}
            {isResidentFlow && isShopOpened && (
              <NoteTextarea
                label={t('market-shop:productDetails.noteLabel')}
                placeholder={t('market-shop:productDetails.notePlaceholder')}
                maxLength={MAX_NOTE_LENGTH}
                value={localNote}
                disabled={isInTransition}
                onChange={setLocalNote}
              />
            )}
          </S.Content>
          <S.Controls $grid={isShopOpened}>
            <Button
              use="secondary-gray"
              size="large"
              onlyBox={isShopOpened}
              disabled={isInTransition}
              onClick={onClose}
            >
              <Icon icon={CaretLeftIcon} />
              {!isShopOpened && t('market-shop:productDetails.back')}
            </Button>
            {isShopOpened && (
              <Button
                use="primary"
                size="large"
                disabled={
                  isInTransition ||
                  (product.quantity <= 0 && !product.unlimited) ||
                  localNote.length > MAX_NOTE_LENGTH
                }
                onClick={handleResidentAddToCart}
              >
                {isAlreadyInCart
                  ? t('market-shop:productDetails.saveChanges')
                  : t('market-shop:productDetails.addToCart')}
              </Button>
            )}
          </S.Controls>
        </>
      )}
    </PopupWindow>
  )
}
